import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Button,
    Select,
    TextField,
    MenuItem,
    FormControl,
    FormControlLabel,
    InputLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormGroup,
    OutlinedInput,
    Divider,
    InputAdornment,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/system';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { validateInputValue } from 'utils/commonHelpers';
import ApiHelper from '../../../utils/apiHelper';

const AddSchemeDialog = ({ onClose, inputFields, timeIntervals }) => {
    const theme = useTheme();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [performanceOptions, setPerformanceOptions] = useState([]);
    const [days, setDays] = useState({
        sunday: false,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false
    });
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [advancedSettings, setAdvancedSettings] = useState({
        displayInactivityTimeoutAC: 900,
        displayInactivityTimeoutDC: 600,
        cpuInactivityTimeoutAC: 1800,
        cpuInactivityTimeoutDC: 1200,
        usbSleepAC: 1,
        usbSleepDC: 1,
        wakeTimersAC: 1,
        wakeTimersDC: 1,
        hidWake: 0,
        cpuMax: 25,
        diskMax: 25,
        networkMax: 2500,
        screenDim: 100,
        brightness: 255,
        performance: 'balanced',
        update_poll: 0
    });
    const [disabled, setDisabled] = useState(true);

    useEffect(() => {
        getPerformanceOptions();
        if (
            name === '' ||
            startTime === '' ||
            endTime === '' ||
            advancedSettings.cpuMax === '' ||
            advancedSettings.diskMax === '' ||
            advancedSettings.networkMax === '' ||
            advancedSettings.screenDim === '' ||
            advancedSettings.brightness === ''
        ) {
            setDisabled(true);
        } else {
            setDisabled(false);
        }
    }, [name, startTime, endTime, advancedSettings]);

    const getPerformanceOptions = () => {
        ApiHelper.get('/api/settings/performance_options/')
            .then((res) => {
                if (res.status === 200) {
                    setPerformanceOptions(res.data);
                } else if (res.status === 404) {
                    toast.error(res.data.message);
                }
            })
            .catch((err) => {});
    };

    const handleChangeDays = (e) => {
        setDays({
            ...days,
            [e.target.name]: e.target.checked
        });
    };

    const handleChangeName = (e) => {
        if (e.target.value.length > inputFields['name']) {
            toast.warning('This field must be ' + inputFields['name'] + ' characters or less.');
        } else {
            setName(e.target.value);
        }
    };

    const handleChangeDesc = (e) => {
        if (e.target.value.length > inputFields['desc']) {
            toast.warning('This field must be ' + inputFields['desc'] + ' characters or less.');
        } else {
            setDescription(e.target.value);
        }
    };

    const handleChangeAdvancedSettings = (e) => {
        setAdvancedSettings({
            ...advancedSettings,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = () => {
        if (
            validateInputValue(advancedSettings.cpuMax, 'percentage') !== true ||
            validateInputValue(advancedSettings.diskMax, 'percentage') !== true ||
            validateInputValue(advancedSettings.screenDim, 'percentage') !== true
        ) {
            toast.error('Percentage value should be between 0 and 100');
            return true;
        }
        if (validateInputValue(advancedSettings.brightness, 'brightness') !== true) {
            toast.error('It should be between 0 and 255');
            return true;
        }
        if (endTime < startTime) {
            toast.error('End time should be greater than start time');
            return true;
        }
        if (
            days.sunday === false &&
            days.monday === false &&
            days.tuesday === false &&
            days.wednesday === false &&
            days.thursday === false &&
            days.friday === false &&
            days.saturday === false
        ) {
            toast.error('Please select the days');
            return true;
        }
        const newSchemeData = {
            schemeName: name,
            schemeDescription: description,
            days: days,
            startTime: startTime,
            endTime: endTime,
            advancedSettings: advancedSettings
        };
        if (localStorage.getItem('policySchemeData')) {
            const schemeData = JSON.parse(localStorage.getItem('policySchemeData'));
            const newData = JSON.stringify([...schemeData, newSchemeData]);
            localStorage.setItem('policySchemeData', newData);
        } else {
            const newData = JSON.stringify([newSchemeData]);
            localStorage.setItem('policySchemeData', newData);
        }
        onClose();
    };

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle sx={{ fontSize: '16px' }}>Add New Scheme</DialogTitle>
            <DialogContent sx={{ maxWidth: '600px' }}>
                <Box sx={{ mt: 2 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <TextField sx={{ width: '100%' }} label="Scheme Name" value={name} onChange={(e) => handleChangeName(e)} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                sx={{ width: '100%' }}
                                label="Scheme Description"
                                value={description}
                                onChange={(e) => handleChangeDesc(e)}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <FormGroup aria-label="position" row>
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="sunday" checked={days.sunday} onChange={handleChangeDays} />}
                            label="Sun"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="monday" checked={days.monday} onChange={handleChangeDays} />}
                            label="Mon"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="tuesday" checked={days.tuesday} onChange={handleChangeDays} />}
                            label="Tue"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="wednesday" checked={days.wednesday} onChange={handleChangeDays} />}
                            label="Wed"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="thursday" checked={days.thursday} onChange={handleChangeDays} />}
                            label="Thr"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="friday" checked={days.friday} onChange={handleChangeDays} />}
                            label="Fri"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="saturday" checked={days.saturday} onChange={handleChangeDays} />}
                            label="Sat"
                            labelPlacement="top"
                        />
                    </FormGroup>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <Typography>Start Time</Typography>
                            <input
                                type="time"
                                style={{ width: '100%', padding: 3, fontSize: '16px' }}
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography>End Time</Typography>
                            <input
                                type="time"
                                style={{ width: '100%', padding: 3, fontSize: '16px' }}
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <Divider sx={{ border: `1px solid ${theme.palette.primary.main}` }} />
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel>Display Inactivity Timeout (Plugged in)</InputLabel>
                                <Select
                                    label="Display Inactivity Timeout (Plugged in)"
                                    name="displayInactivityTimeoutAC"
                                    value={advancedSettings.displayInactivityTimeoutAC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {timeIntervals.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel>Display Inactivity Timeout (On Battery)</InputLabel>
                                <Select
                                    label="Display Inactivity Timeout (On Battery)"
                                    name="displayInactivityTimeoutDC"
                                    value={advancedSettings.displayInactivityTimeoutDC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {timeIntervals.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel>CPU Inactivity Timeout (Plugged in)</InputLabel>
                                <Select
                                    label="CPU Inactivity Timeout (Plugged in)"
                                    name="cpuInactivityTimeoutAC"
                                    value={advancedSettings.cpuInactivityTimeoutAC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {timeIntervals.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel>CPU Inactivity Timeout (On Battery)</InputLabel>
                                <Select
                                    label="CPU Inactivity Timeout (On Battery)"
                                    name="cpuInactivityTimeoutDC"
                                    value={advancedSettings.cpuInactivityTimeoutDC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {timeIntervals.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
                <Divider sx={{ border: `3px solid ${theme.palette.secondary.main}` }} />
                <Box sx={{ mt: 2 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={6} sx={{ mb: 1 }}>
                            <FormControl sx={{ width: '49%' }}>
                                <InputLabel>USB Sleep (AC)</InputLabel>
                                <Select
                                    label="USB Sleep (AC)"
                                    name="usbSleepAC"
                                    value={advancedSettings.usbSleepAC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    <MenuItem value="1">Enabled</MenuItem>
                                    <MenuItem value="0">Disabled</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl sx={{ width: '49%', ml: '4px' }}>
                                <InputLabel>USB Sleep (DC)</InputLabel>
                                <Select
                                    label="USB Sleep (DC)"
                                    name="usbSleepDC"
                                    value={advancedSettings.usbSleepDC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    <MenuItem value="1">Enabled</MenuItem>
                                    <MenuItem value="0">Disabled</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '49%' }}>
                                <InputLabel>Wake Timers (AC)</InputLabel>
                                <Select
                                    label="Wake Timers (AC)"
                                    name="wakeTimersAC"
                                    value={advancedSettings.wakeTimersAC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    <MenuItem value="1">Enabled</MenuItem>
                                    <MenuItem value="0">Disabled</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl sx={{ width: '49%', ml: '4px' }}>
                                <InputLabel>Wake Timers (DC)</InputLabel>
                                <Select
                                    label="Wake Timers (DC)"
                                    name="wakeTimersDC"
                                    value={advancedSettings.wakeTimersDC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    <MenuItem value="1">Enabled</MenuItem>
                                    <MenuItem value="0">Disabled</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '49%' }}>
                                <InputLabel>HID Wake</InputLabel>
                                <Select
                                    label="HID Wake"
                                    name="hidWake"
                                    value={advancedSettings.hidWake}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    <MenuItem value="1">Enabled</MenuItem>
                                    <MenuItem value="0">Disabled</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl sx={{ width: '49%', ml: '4px' }}>
                                <InputLabel>Performance</InputLabel>
                                <Select
                                    label="Performance"
                                    name="performance"
                                    value={advancedSettings.performance}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {performanceOptions.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '49%' }}>
                                <InputLabel htmlFor="brightness">Brightness</InputLabel>
                                <OutlinedInput
                                    id="brightness"
                                    name="brightness"
                                    type="number"
                                    label="Brightness"
                                    value={advancedSettings.brightness}
                                    onChange={handleChangeAdvancedSettings}
                                />
                            </FormControl>
                            <FormControl sx={{ width: '49%', ml: '4px' }}>
                                <InputLabel htmlFor="screenDim">Screen Dim</InputLabel>
                                <OutlinedInput
                                    id="screenDim"
                                    name="screenDim"
                                    type="number"
                                    label="Screen Dim"
                                    value={advancedSettings.screenDim}
                                    onChange={handleChangeAdvancedSettings}
                                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '49%' }}>
                                <InputLabel htmlFor="cpuMax">CPU Max</InputLabel>
                                <OutlinedInput
                                    id="cpuMax"
                                    name="cpuMax"
                                    label="CPU Max"
                                    type="number"
                                    value={advancedSettings.cpuMax}
                                    onChange={handleChangeAdvancedSettings}
                                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                    // aria-describedby="outlined-weight-helper-text"
                                    // inputProps={{
                                    //     'aria-label': ''
                                    // }}
                                />
                            </FormControl>
                            <FormControl sx={{ width: '49%', ml: '4px' }}>
                                <InputLabel htmlFor="diskMax">Disk Max</InputLabel>
                                <OutlinedInput
                                    id="diskMax"
                                    name="diskMax"
                                    label="Disk Max"
                                    type="number"
                                    value={advancedSettings.diskMax}
                                    onChange={handleChangeAdvancedSettings}
                                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '49%' }}>
                                <InputLabel htmlFor="networkMax">Network Max</InputLabel>
                                <OutlinedInput
                                    id="networkMax"
                                    name="networkMax"
                                    type="number"
                                    label="Network Max"
                                    value={advancedSettings.networkMax}
                                    onChange={handleChangeAdvancedSettings}
                                    endAdornment={<InputAdornment position="end">pkt/s</InputAdornment>}
                                />
                            </FormControl>
                            <FormControl sx={{ width: '49%', ml: '4px' }}>
                                <InputLabel>SCCM Poll</InputLabel>
                                <Select
                                    label="SCCM Poll"
                                    name="update_poll"
                                    value={advancedSettings.update_poll}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    <MenuItem value="1">Enabled</MenuItem>
                                    <MenuItem value="0">Disabled</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button disabled={disabled} onClick={handleSubmit}>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

AddSchemeDialog.propTypes = {
    onClose: PropTypes.func,
    inputFields: PropTypes.object,
    timeIntervals: PropTypes.array
};

export default AddSchemeDialog;
